import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';

const Transfer = graphql(`
  mutation TransferOrganization($organizationId: uuid!, $newOwnerId: String!) {
    transfer_organization(
      args: { organization_id: $organizationId, new_owner_id: $newOwnerId }
    ) {
      id
    }
  }
`);

const GetContext = graphql(`
  query GetOrganizationTransferConfirmContext(
    $organizationId: uuid!
    $personId: String!
  ) {
    event_by_pk(id: $organizationId) {
      id
      title
      owner_id
      event_type
    }
    person_by_pk(id: $personId) {
      id
      name
    }
  }
`);

export default function OrganizationTransferConfirm() {
  const { organizationId, transferId } = useLocalSearchParams<{
    organizationId: string;
    transferId: string;
  }>();
  const { dismissTo } = useRouter();
  const [, transfer] = useMutation(Transfer);
  const [result, refresh] = useQuery({
    query: GetContext,
    variables: { organizationId, personId: transferId },
  });
  const userId = useUserId();
  const entity = result.data?.event_by_pk;
  const recipient = result.data?.person_by_pk;

  const onTransfer = async () => {
    const result = await transfer({
      organizationId,
      newOwnerId: transferId,
    });
    if (result.error || !result.data?.transfer_organization?.id)
      return { success: false };
    return { success: true };
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: entity?.title ? `Transfer ${entity.title}` : 'Transfer',
        }}
      />
      <ContentBoundary
        data={
          entity?.title &&
          recipient?.name &&
          entity.owner_id === userId &&
          entity.event_type === 'organization'
            ? {
                entity: { id: entity.id, title: entity.title },
                recipient: { id: recipient.id, name: recipient.name },
              }
            : null
        }
        loading={result.fetching}
        error={result.error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={
          <EmptyPlaceholder message="Ownership transfer is unavailable." />
        }
      >
        {({ entity, recipient }) => (
          <DestructiveAction
            key={`${entity.id}:${recipient.id}`}
            confirmationName={entity.title}
            label="Transfer ownership"
            submittingLabel="Transferring…"
            onAction={onTransfer}
            onSuccess={() => {
              dismissTo({
                pathname: '/organization/members',
                params: { organizationId },
              });
            }}
            testID="OrganizationTransferConfirmButton"
          >
            <DestructiveActionHeading>
              Transfer ownership of {entity.title} to {recipient.name}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This gives the recipient full control. You cannot undo this action
              yourself.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
